// src/Dashboard/pages/teacher/PrepaPhysique/components/AssignmentEditDrawer.jsx

import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaCheck,
  FaDumbbell,
  FaSave,
  FaTimes,
  FaUser,
} from "react-icons/fa";

import {
  formatDateFR,
  getAdherentName,
} from "../utils/prepaPhysiqueUtils";

const initialForm = {
  date_realisation: new Date().toISOString().slice(0, 10),
  repetitions: "",
  is_done: false,
};

function toInputDate(dateValue) {
  if (!dateValue) return new Date().toISOString().slice(0, 10);

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return new Date().toISOString().slice(0, 10);
  }

  return date.toISOString().slice(0, 10);
}

export default function AssignmentEditDrawer({
  isOpen,
  assignment,
  saving = false,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (!isOpen || !assignment) return;

    setForm({
      date_realisation: toInputDate(assignment?.date_realisation),
      repetitions: assignment?.repetitions ?? "",
      is_done: !!assignment?.is_done,
    });

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, assignment, onClose]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit?.(form);
  };

  if (!isOpen || !assignment) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[100]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Modifier l’affectation
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white">
              {assignment?.Exercice?.nom || "Exercice"}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <InfoCard
                  icon={<FaUser />}
                  label="Adhérent"
                  value={getAdherentName(assignment?.Adherent)}
                />

                <InfoCard
                  icon={<FaDumbbell />}
                  label="Exercice"
                  value={assignment?.Exercice?.nom || "—"}
                  subValue={assignment?.Exercice?.programme || ""}
                />
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                <h3 className="mb-4 font-bold text-slate-900 dark:text-white">
                  Suivi de réalisation
                </h3>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Date de réalisation
                    </label>

                    <input
                      type="date"
                      value={form.date_realisation}
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          date_realisation: event.target.value,
                        }))
                      }
                      required
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Répétitions
                    </label>

                    <input
                      type="number"
                      min="0"
                      value={form.repetitions}
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          repetitions: event.target.value,
                        }))
                      }
                      placeholder="Nombre de répétitions"
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />
                  </div>
                </div>

                <label className="mt-4 flex items-center gap-3 rounded-2xl bg-white px-3 py-3 text-sm font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700">
                  <input
                    type="checkbox"
                    checked={form.is_done}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        is_done: event.target.checked,
                      }))
                    }
                    className="h-5 w-5 accent-emerald-600"
                  />

                  <span className="inline-flex items-center gap-2">
                    <FaCheck className="text-emerald-500" />
                    Exercice effectué
                  </span>
                </label>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 text-sm dark:border-slate-800 dark:bg-slate-950/60">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Affectation actuelle
                </p>

                <p className="mt-2 text-slate-600 dark:text-slate-300">
                  Date enregistrée :{" "}
                  <span className="font-bold text-slate-900 dark:text-white">
                    {formatDateFR(assignment?.date_realisation)}
                  </span>
                </p>

                <p className="mt-1 text-slate-600 dark:text-slate-300">
                  Statut :{" "}
                  <span className="font-bold text-slate-900 dark:text-white">
                    {assignment?.is_done ? "Fait" : "Non fait"}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white/95 p-4 dark:border-slate-800 dark:bg-slate-900/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>,
    document.body
  );
}

function InfoCard({ icon, label, value, subValue }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          {icon}
        </span>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {label}
          </p>

          <p className="mt-1 truncate text-sm font-bold text-slate-900 dark:text-white">
            {value || "—"}
          </p>

          {subValue ? (
            <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
              {subValue}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}